import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {createRoute, fetchRoutes, updateRoute} from "./routeApi";
import {
    getExcludedOrderIds,
    getSaveNotificationMessage,
    isSaveBlocked,
} from "./routeSaveResponse";
import {showGlobalNotification} from "../components/NotificationContext";
import _ from 'lodash'

const initialState = {
    drivesInRoutes: [],
    saveRouteInProcess: false,
    routeInSaveProcess: [],
    routeIds: [],
    loadRoutesInProcess: false
};

export const loadRoutesAsync = createAsyncThunk(
    'drycleaning/routes',
    async (params= {}) => {
        const response = await fetchRoutes(params);
        return response.data;
    }
);

export const createRouteAsync = createAsyncThunk(
    'drycleaning/route/create',
    async (params = {}, {rejectWithValue}) => {
        const response = await createRoute(params);
        const data = response.data;

        if (isSaveBlocked(data)) {
            return rejectWithValue(data);
        }

        return data;
    }
);

export const updateRouteAsync = createAsyncThunk(
    'drycleaning/route/update',
    async (params = {}, {rejectWithValue}) => {
        const response = await updateRoute(params);
        const data = response.data;

        if (isSaveBlocked(data)) {
            return rejectWithValue(data);
        }

        return data;
    }
);

function notifySaveResult(data, severity) {

    // нотификации вызываются в src/features/drycleaning/route/routeApi.js
    // в методах updateRoute и createRoute. если оставить здесь -
    // то перетирает стандартной ошибкой
    return;

    const message = getSaveNotificationMessage(data);
    if (!message) {
        return;
    }
    if (typeof showGlobalNotification === 'function') {
        showGlobalNotification(message, severity);
    } else {
        alert(message);
    }
}

export const routeSlice = createSlice({
    name: 'route',
    initialState,
    reducers: {
        addRouteInSaveProcess(state, action) {
            state.routeInSaveProcess = action.payload
        },
        updateRouteIdInRoutesInSaveProcess(state, action) {
            state.routeInSaveProcess.forEach((driveInRouteInSaveProcess) => {
                const driveInRoute = _.find(state.drivesInRoutes, {drive: driveInRouteInSaveProcess.drive});
                if (driveInRoute) {
                    driveInRoute.route_id = action.payload.route_id;
                }
            })
        },
        clearRouteInSaveProcess(state, action) {
            state.routeInSaveProcess = [];
        },
        updateDriveInRoute(state, action) {
            const driveInRouteInState = _.find(state.drivesInRoutes, (driveInRoute) => {
                return driveInRoute.drive.id === action.payload.drive.id
            })
            if (typeof driveInRouteInState !== "undefined") {
                _.each(action.payload.attributes, (value, key) => {
                    driveInRouteInState.drive[key] = value;
                })
            }
        },
        addToRoute: (state, action) => {
            const payload = action.payload;
            const exist = _.find(state.drivesInRoutes, function (driveInRoute) {
                return _.isEqual(driveInRoute.drive.id, payload.drive.id)
            });

            if (typeof exist !== "undefined") {
                return;
            }

            if (payload.courier) {
                state.drivesInRoutes.push(payload);
            }
        },
        removeFromRoute(state, action) {
            let driveInRouteInState = _.find(state.drivesInRoutes, function (driveInRoute) {
                return _.isEqual(driveInRoute.drive.id, action.payload.drive.id)
            });
            if (typeof driveInRouteInState !== "undefined") {
                state.drivesInRoutes = _.without(state.drivesInRoutes, driveInRouteInState);
            }
        },
        removeExcludedOrdersFromCourierRoute(state, action) {
            const courier = action.payload.courier;
            const excludedOrderIds = new Set(
                (action.payload.orderIds || []).map((id) => Number(id))
            );

            if (!excludedOrderIds.size) {
                return;
            }

            state.drivesInRoutes = state.drivesInRoutes.filter((driveInRoute) => {
                if (driveInRoute.courier !== courier) {
                    return true;
                }
                const orderNumber = driveInRoute.drive?.orderNumber;
                if (orderNumber === undefined || orderNumber === null) {
                    return true;
                }
                return !excludedOrderIds.has(Number(orderNumber));
            });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadRoutesAsync.pending, (state, action) => {
                state.loadRoutesInProcess = true;
            });
        builder
            .addCase(loadRoutesAsync.rejected, (state, action) => {
                state.loadRoutesInProcess = false;
            });
        builder
            .addCase(loadRoutesAsync.fulfilled, (state, action) => {
                state.loadRoutesInProcess = false;
                state.drivesInRoutes = action.payload;
                state.routeIds = [];
                _.each(state.drivesInRoutes, (driveInRoute) => {
                    const routeId = _.find(state.routeIds, {
                        id: driveInRoute.route_id,
                        employeeId: driveInRoute.courier,
                        date: driveInRoute.date
                    });
                    if (typeof routeId === "undefined") {
                        state.routeIds.push({
                            employeeId: driveInRoute.courier,
                            id: driveInRoute.route_id,
                            date: driveInRoute.date
                        })
                    }
                })
            });
        builder
            .addCase(createRouteAsync.pending, (state, action) => {
                state.saveRouteInProcess = true;
            });
        builder
            .addCase(updateRouteAsync.pending, (state, action) => {
                state.saveRouteInProcess = true;
            });
        builder
            .addCase(createRouteAsync.fulfilled, (state, action) => {
                state.saveRouteInProcess = false;
                const payload = action.payload;
                const employeeId = state.routeInSaveProcess[0]?.courier;

                if (employeeId != null) {
                    const excludedOrderIds = getExcludedOrderIds(payload);
                    if (excludedOrderIds.length) {
                        routeSlice.caseReducers.removeExcludedOrdersFromCourierRoute(state, {
                            payload: {courier: employeeId, orderIds: excludedOrderIds},
                        });
                    }
                }

                if (payload.route_id != null && employeeId != null) {
                    const routeDate = action.meta?.arg?.date || state.routeInSaveProcess[0]?.date;
                    state.routeIds.push({
                        employeeId: employeeId,
                        id: payload.route_id,
                        date: routeDate,
                    });
                }

                routeSlice.caseReducers.updateRouteIdInRoutesInSaveProcess(state, action);
                routeSlice.caseReducers.clearRouteInSaveProcess(state, action);

                if (payload.warnings?.length) {
                    notifySaveResult(payload, 'warning');
                }
            });
        builder
            .addCase(createRouteAsync.rejected, (state, action) => {
                state.saveRouteInProcess = false;
                const employeeId = state.routeInSaveProcess[0]?.courier;

                if (employeeId != null && action.payload && typeof action.payload === 'object') {
                    const excludedOrderIds = getExcludedOrderIds(action.payload);
                    if (excludedOrderIds.length) {
                        routeSlice.caseReducers.removeExcludedOrdersFromCourierRoute(state, {
                            payload: {courier: employeeId, orderIds: excludedOrderIds},
                        });
                    }
                }

                routeSlice.caseReducers.clearRouteInSaveProcess(state, action);

                if (action.payload && typeof action.payload === 'object') {
                    notifySaveResult(action.payload, 'error');
                } else if (action.error?.message) {
                    notifySaveResult({error: [action.error.message]}, 'error');
                }
            });
        builder
            .addCase(updateRouteAsync.fulfilled, (state, action) => {
                state.saveRouteInProcess = false;
                const payload = action.payload;
                const employeeId = state.routeInSaveProcess[0]?.courier;

                if (employeeId != null) {
                    const excludedOrderIds = getExcludedOrderIds(payload);
                    if (excludedOrderIds.length) {
                        routeSlice.caseReducers.removeExcludedOrdersFromCourierRoute(state, {
                            payload: {courier: employeeId, orderIds: excludedOrderIds},
                        });
                    }
                }

                routeSlice.caseReducers.updateRouteIdInRoutesInSaveProcess(state, action);
                routeSlice.caseReducers.clearRouteInSaveProcess(state, action);

                if (payload.warnings?.length) {
                    notifySaveResult(payload, 'warning');
                }
            });
        builder
            .addCase(updateRouteAsync.rejected, (state, action) => {
                state.saveRouteInProcess = false;
                const employeeId = state.routeInSaveProcess[0]?.courier;

                if (employeeId != null && action.payload && typeof action.payload === 'object') {
                    const excludedOrderIds = getExcludedOrderIds(action.payload);
                    if (excludedOrderIds.length) {
                        routeSlice.caseReducers.removeExcludedOrdersFromCourierRoute(state, {
                            payload: {courier: employeeId, orderIds: excludedOrderIds},
                        });
                    }
                }

                routeSlice.caseReducers.clearRouteInSaveProcess(state, action);

                if (action.payload && typeof action.payload === 'object') {
                    notifySaveResult(action.payload, 'error');
                } else if (action.error?.message) {
                    notifySaveResult({error: [action.error.message]}, 'error');
                }
            });
    },
});

export const selectDrivesInRoutes = (state) => state.drycleaningRoute.drivesInRoutes;
export const selectSaveRouteInProcess = (state) => state.drycleaningRoute.saveRouteInProcess;
export const selectRouteIds = (state) => state.drycleaningRoute.routeIds;
export const selectLoadRoutesInProcess = (state) => state.drycleaningRoute.loadRoutesInProcess;
export const {addToRoute, removeFromRoute, addRouteInSaveProcess, updateDriveInRoute} = routeSlice.actions;

export default routeSlice.reducer;
